import { FORECAST_INITIAL_VALUE, FORECAST_LENGTH } from '@/coreDomainsModule/constants';
import { IAnnounce, assertsAnnounceKey } from '../entities/forecast';
import { initialAnnounce } from '@/appLogic/importData/initialData';
import { IYearMonth } from '../date';
import { DateService } from './dateService';

/**
 * 内示を操作するためのサービス
 */
export class ForecastService {
  /**
   * 内示のAnnounceオブジェクトのyear, monthをセットする
   * @param announce - 内示情報
   * @param date - 開始する日付の情報
   * @param ignoreCurrentMonth -当月を無視するかどうか
   * @param forecastLength - 内示の長さ
   */
  public static setAnnounceYearMonth(announce: IAnnounce[], date: Date, ignoreCurrentMonth = false, forecastLength = FORECAST_LENGTH) {
    const initialAnnounce: IAnnounce = {
      amount: FORECAST_INITIAL_VALUE,
      year: 0,
      month: 0,
      manageInitiatedYearMonth: undefined,
      createAt: undefined,
      updateAt: undefined,
    };

    // NOTE: dayが悪さをするので、1日に設定(10/31 + 1month -> 12/1 とかになってしまうため)
    date.setDate(1);

    if (announce.length === 0) {
      for (let i = 0; i < forecastLength; i++) {
        announce.push(Object.assign({}, initialAnnounce));
      }
    }

    if (ignoreCurrentMonth) {
      date.setMonth(date.getMonth() + 1);
    }

    announce.map((a) => {
      a.year = date.getFullYear();
      date.setMonth(date.getMonth() + 1);
      a.month = date.getMonth();
      if (date.getMonth() === 0) {
        a.month = 12;
      }
    });
  }

  public static initialAnnounce: IAnnounce = {
    amount: FORECAST_INITIAL_VALUE,
    year: 0,
    month: 0,
    manageInitiatedYearMonth: undefined,
    createAt: undefined,
    updateAt: undefined,
  };

  /**
   *  yearとmonthを元に、announceのamountを取得
   * @param announce - 内示情報
   * @param yearMonthInfo - 年月情報
   * @returns number - amount
   */
  public static getAnnounceValue = (announce: IAnnounce[], yearMonthInfo: IYearMonth): number => {
    const target = announce.find((x) => DateService.equalYearMonthInfo(x, yearMonthInfo));
    return target?.amount || 0;
  };

  /**
   * AnnounceKey型をFORECAST_LENGTH(number型)に基づいて、
   * announce-${number}で生成
   */
  public static announceKeys = new Array(FORECAST_LENGTH).fill('announce').map((x, i) => {
    const value = x + '-' + (i + 1);
    assertsAnnounceKey(value);
    return value;
  });

  /**
   * IAnnounceのソートを行う
   * @param announce - 内示情報
   * @param asc - 昇順か降順か
   */
  public static sortAnnounce = (announce: IAnnounce[], asc = true): IAnnounce[] => {
    if (asc) {
      return announce.sort((a: IAnnounce, b: IAnnounce) => {
        const A = a.year * 10000 + a.month * 100;
        const B = b.year * 10000 + b.month * 100;
        return A < B ? -1 : 1;
      });
    } else {
      return announce.sort((a: IAnnounce, b: IAnnounce) => {
        const A = a.year * 10000 + a.month * 100;
        const B = b.year * 10000 + b.month * 100;
        return A < B ? 1 : -1;
      });
    }
  };

  /**
   * 対象年月ごとに、受領年月が最新の客先内示を取得する
   * @param forecasts - 内示
   */
  public static extractLatestReceivedCustomerForecasts = (forecasts: readonly IAnnounce[]): IAnnounce[] => {
    const latestByTargetYearMonth = new Map<string, IAnnounce>();

    forecasts.forEach((forecast) => {
      const key = `${forecast.year}-${forecast.month}`;
      const current = latestByTargetYearMonth.get(key);

      if (!current) {
        latestByTargetYearMonth.set(key, forecast);
        return;
      }

      const receivedYearMonth = forecast.manageInitiatedYearMonth;
      const currentReceivedYearMonth = current.manageInitiatedYearMonth;

      // 受領年月ありを、受領年月なしより優先する
      if (!receivedYearMonth) return;

      if (!currentReceivedYearMonth || DateService.compareYearMonths(receivedYearMonth, currentReceivedYearMonth) >= 0) {
        latestByTargetYearMonth.set(key, forecast);
      }
    });

    return [...latestByTargetYearMonth.values()].sort((a, b) => DateService.compareYearMonths(a, b));
  };
}
